import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import styled from 'styled-components';
import { database } from '../firebase/firebaseConfig';
import { updateSingleTransactionWithPaidBalanceAdjustment } from '../firebase/firebaseUtils';
import EditTransactionModal from './EditTransactionModal';
import {
  Button,
  ClearButton,
  Input,
  InputWrapper,
  ModalContainer,
  ModalContent,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from './StyledComponents';

const TRANSACTION_WINDOW_SIZE = 5;
const MAX_NAME_LENGTH = 30;

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const parseTransactionDate = (date) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const getPaidStatusMatch = (text, isPaid) => {
  if (['paid', 'yes', 'true'].includes(text)) return Boolean(isPaid);
  if (['unpaid', 'not paid', 'no', 'false'].includes(text)) return !isPaid;
  return null;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

const truncateName = (value) => {
  const name = value || '';
  if (name.length <= MAX_NAME_LENGTH) return name;
  return `${name.slice(0, MAX_NAME_LENGTH)}...`;
};

const AccountTransactionsModal = ({ isOpen, onClose, account, accounts, userId, onEditAccount }) => {
  const [transactions, setTransactions] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [isEditTransactionModalOpen, setIsEditTransactionModalOpen] = useState(false);
  const [transactionWindow, setTransactionWindow] = useState(() => {
    return {
      past: TRANSACTION_WINDOW_SIZE,
      future: TRANSACTION_WINDOW_SIZE,
    };
  });
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setTransactionWindow({
      past: TRANSACTION_WINDOW_SIZE,
      future: TRANSACTION_WINDOW_SIZE,
    });
    setFilterText('');
    setEditingTransaction(null);
    setIsEditTransactionModalOpen(false);
  }, [isOpen, account?.id]);

  useEffect(() => {
    if (!isOpen || !userId) return undefined;

    const transactionsRef = ref(database, `users/${userId}/transactions`);
    const unsubscribe = onValue(transactionsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const allTransactions = [];

      Object.entries(data).forEach(([key, value]) => {
        if (value.childTransactions) {
          Object.entries(value.childTransactions).forEach(([childKey, childValue]) => {
            allTransactions.push({
              id: childKey,
              parentId: key,
              recurrence: value.recurrence,
              recurrenceEndDate: value.recurrenceEndDate,
              ...childValue,
            });
          });
        } else {
          allTransactions.push({ id: key, ...value });
        }
      });

      allTransactions.sort((a, b) => {
        const dateDiff = new Date(a.date) - new Date(b.date);
        if (dateDiff !== 0) return dateDiff;
        return Number(b.amount || 0) - Number(a.amount || 0);
      });
      setTransactions(allTransactions);
    });

    return unsubscribe;
  }, [isOpen, userId]);

  const accountTransactions = useMemo(() => {
    if (!account?.id) return [];

    return transactions.filter((transaction) => (
      transaction.fromAccount === account.id || transaction.toAccount === account.id
    ));
  }, [account?.id, transactions]);

  const transactionBuckets = useMemo(() => {
    const today = startOfToday();
    const past = [];
    const future = [];

    accountTransactions.forEach((transaction) => {
      const transactionDate = parseTransactionDate(transaction.date);
      if (!transactionDate) return;
      if (transactionDate <= today) {
        past.push(transaction);
      } else {
        future.push(transaction);
      }
    });

    return { past, future };
  }, [accountTransactions]);

  const windowedTransactions = useMemo(() => {
    const pastTransactions = transactionBuckets.past.slice(-transactionWindow.past);
    const futureTransactions = transactionBuckets.future.slice(0, transactionWindow.future);
    return [...pastTransactions, ...futureTransactions];
  }, [transactionBuckets, transactionWindow]);

  const getRelatedAccountName = (transaction) => {
    if (transaction.fromAccount === account?.id) {
      return accounts?.[transaction.toAccount]?.name || '';
    }
    return accounts?.[transaction.fromAccount]?.name || '';
  };

  const visibleTransactions = useMemo(() => {
    const text = filterText.trim().toLowerCase();

    return windowedTransactions.filter((transaction) => {
      if (!text) return true;

      const amount = Number(transaction.amount || 0);
      const paidStatusMatch = getPaidStatusMatch(text, transaction.isPaid);
      if (paidStatusMatch !== null) return paidStatusMatch;

      const relatedAccountName = transaction.fromAccount === account?.id
        ? accounts?.[transaction.toAccount]?.name
        : accounts?.[transaction.fromAccount]?.name;
      const searchBlob = [
        transaction.description,
        amount.toString(),
        formatCurrency(amount),
        relatedAccountName,
      ].filter(Boolean).join(' ').toLowerCase();

      return searchBlob.includes(text);
    });
  }, [account?.id, accounts, filterText, windowedTransactions]);

  const formatDate = (date) => {
    const parsed = parseTransactionDate(date);
    return parsed ? parsed.toLocaleDateString() : '';
  };

  const loadEarlierTransactions = (scrollTarget = null) => {
    const previousHeight = scrollTarget?.scrollHeight;
    setTransactionWindow((current) => ({
      ...current,
      past: current.past + TRANSACTION_WINDOW_SIZE,
    }));
    if (scrollTarget && previousHeight) {
      window.requestAnimationFrame(() => {
        scrollTarget.scrollTop = scrollTarget.scrollHeight - previousHeight + scrollTarget.scrollTop;
      });
    }
  };

  const loadLaterTransactions = () => {
    setTransactionWindow((current) => ({
      ...current,
      future: current.future + TRANSACTION_WINDOW_SIZE,
    }));
  };

  const handleScroll = (event) => {
    const target = event.currentTarget;
    if (target.scrollTop < 40) {
      loadEarlierTransactions(target);
    }

    if (target.scrollTop + target.clientHeight > target.scrollHeight - 40) {
      loadLaterTransactions();
    }
  };

  const handleWheel = (event) => {
    const target = event.currentTarget;
    if (event.deltaY < 0 && target.scrollTop <= 0) {
      loadEarlierTransactions(target);
    }

    if (event.deltaY > 0 && target.scrollTop + target.clientHeight >= target.scrollHeight - 1) {
      loadLaterTransactions();
    }
  };

  const openEditTransactionModal = (transaction) => {
    setEditingTransaction(transaction);
    setIsEditTransactionModalOpen(true);
  };

  const closeEditTransactionModal = () => {
    setEditingTransaction(null);
    setIsEditTransactionModalOpen(false);
  };

  const handleSaveTransaction = async (updatedTransaction) => {
    try {
      await updateSingleTransactionWithPaidBalanceAdjustment(
        userId,
        editingTransaction,
        updatedTransaction
      );
      closeEditTransactionModal();
    } catch (error) {
      console.error('Error updating transaction from account modal: ', error);
    }
  };

  if (!isOpen || !account) return null;

  return (
    <>
      <ModalContainer onClick={onClose}>
        <LargeModalContent onClick={(event) => event.stopPropagation()}>
          <ModalHeader>
            <div>
              <h2>{account.name} Transactions</h2>
              <DateRangeText>
                Showing last {Math.min(transactionWindow.past, transactionBuckets.past.length)} and next {Math.min(transactionWindow.future, transactionBuckets.future.length)} transactions
              </DateRangeText>
            </div>
            <HeaderActions>
              <Button onClick={onEditAccount}>Edit Account</Button>
              <Button onClick={onClose}>Close</Button>
            </HeaderActions>
          </ModalHeader>

          <Toolbar>
            <InputWrapper>
              <Input
                aria-label="Search account transactions"
                placeholder="Search name, amount, paid"
                value={filterText}
                onChange={(event) => setFilterText(event.target.value)}
              />
              {filterText && (
                <ClearButton onClick={() => setFilterText('')} aria-label="Clear search">&times;</ClearButton>
              )}
            </InputWrapper>
            <ResultCount>
              {visibleTransactions.length} of {windowedTransactions.length}
            </ResultCount>
          </Toolbar>

          <TransactionsTableContainer
            ref={scrollContainerRef}
            onScroll={handleScroll}
            onWheel={handleWheel}
          >
            <Table>
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Name</Th>
                  <Th>Amount</Th>
                  <Th>Account</Th>
                  <Th>Paid</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {visibleTransactions.length === 0 ? (
                  <Tr>
                    <Td colSpan={6}>No transactions found for this account in the current window.</Td>
                  </Tr>
                ) : (
                  visibleTransactions.map((transaction) => (
                    <Tr
                      key={`${transaction.parentId || 'single'}-${transaction.id}`}
                      onClick={() => openEditTransactionModal(transaction)}
                    >
                      <Td>{formatDate(transaction.date)}</Td>
                      <Td title={transaction.description || ''}>
                        <TransactionName>{truncateName(transaction.description)}</TransactionName>
                      </Td>
                      <Td>{formatCurrency(Number(transaction.amount || 0))}</Td>
                      <Td>{getRelatedAccountName(transaction)}</Td>
                      <Td>{transaction.isPaid ? 'Paid' : 'Unpaid'}</Td>
                      <Td onClick={(event) => event.stopPropagation()}>
                        <CompactButton onClick={() => openEditTransactionModal(transaction)}>Edit</CompactButton>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TransactionsTableContainer>
        </LargeModalContent>
      </ModalContainer>
      <EditTransactionModal
        transaction={editingTransaction}
        isOpen={isEditTransactionModalOpen}
        onClose={closeEditTransactionModal}
        onSave={handleSaveTransaction}
        accounts={accounts}
        userId={userId}
        allowPaidStatusEdit
        forceSingleEdit
      />
    </>
  );
};

const LargeModalContent = styled(ModalContent)`
  width: 1000px;
  max-width: 94vw;
  height: 86vh;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;

  h2 {
    margin: 0;
  }

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`;

const DateRangeText = styled.div`
  color: ${props => props.theme.secondary};
  font-size: 0.9rem;
  margin-top: 6px;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin: 18px 0 8px;

  ${InputWrapper} {
    width: min(360px, 100%);
  }

  ${Input} {
    margin-bottom: 0;
    padding-right: 34px;
  }

  @media (max-width: 700px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ResultCount = styled.div`
  color: ${props => props.theme.secondary};
  font-size: 0.9rem;
  white-space: nowrap;
`;

const TransactionsTableContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;

  ${Table} {
    margin: 0;
    min-width: 720px;
  }

  ${Thead} {
    position: sticky;
    top: 0;
    z-index: 3;
  }

  ${Th} {
    background-color: ${props => props.theme.primary};
  }
`;

const TransactionName = styled.span`
  display: inline-block;
  max-width: 30ch;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
  white-space: nowrap;
`;

const CompactButton = styled(Button)`
  padding: 6px 10px;
  margin: 0;
`;

export default AccountTransactionsModal;
